import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Add,
  Edit,
  Search,
  AttachMoney,
  FilterList,
  Warning,
  Email,
  EmailOutlined,
  Error,
  Refresh,
  History,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { Donation, CreateDonationRequest, Donor, Collector, Filters, Team } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  PageLayout,
  ScrollableContent,
  PageHeader,
  StandardPagination,
  FilterChip
} from '../components/Layout/index';
import { 
  Button, 
  Chip, 
  DataTable,
  useStatusColors,
  useActionColors,
  ActionButtons
} from '../design-system';
import SecureImage from '../components/SecureImage';
import FileUpload from '../components/FileUpload';
import GenericFilterModal, { FilterField } from '../components/GenericFilterModal';
import ViewModal, { ViewField } from '../components/ViewModal';
import { useFilterState } from '../hooks/useFilterState';
import type { Column } from '../design-system/components/organisms/DataTable/DataTable';

const Donations: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Stati locali per i filtri (senza debounce)
  const [localFilters, setLocalFilters] = useState({
    search: '',
    status: '',
    paymentMethod: '',
    collectorId: '',
    teamId: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
  });
  
  // Stati per la query (con debounce)
  const [queryFilters, setQueryFilters] = useState({
    search: '',
    status: '',
    paymentMethod: '',
    collectorId: '',
    teamId: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
  });
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDonation, setEditingDonation] = useState<Donation | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewingDonation, setViewingDonation] = useState<Donation | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [showReceiptWarning, setShowReceiptWarning] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [donationToDelete, setDonationToDelete] = useState<Donation | null>(null);
  const [formData, setFormData] = useState<CreateDonationRequest & { status?: string }>({
    amount: 0,
    paymentMethod: 'CASH',
    donorId: '',
    collectorId: '',
    notes: '',
    latitude: undefined,
    longitude: undefined,
    address: '',
    status: 'PENDING',
    documentPhotoS3Key: '',
    documentPhotoS3Url: '',
    documentPhotoOriginalName: '',
    documentPhotoMimeType: '',
    documentPhotoFileSize: 0,
  });

  const { hasAnyRole, user } = useAuth();
  const queryClient = useQueryClient();
  const statusColors = useStatusColors();
  const actionColors = useActionColors();

  // Stato per tracciare le email
  const [emailStatuses, setEmailStatuses] = useState<Record<string, any>>({});
  const [sendingEmails, setSendingEmails] = useState<Set<string>>(new Set());

  // Hook per gestire lo stato dei filtri
  const { hasActiveFilters, activeFiltersCount } = useFilterState(queryFilters);

  // Query per ottenere i donatori per autocomplete (solo campi necessari)
  const { data: donors, isLoading: donorsLoading, refetch: refetchDonors } = useQuery({
    queryKey: ['donors-autocomplete'],
    queryFn: () => apiService.getDonorAutocompleteOptions(),
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Query per ottenere i collectors (necessaria per i raccoglitori)
  const { data: collectors, isLoading: collectorsLoading } = useQuery({
    queryKey: ['collectors'],
    queryFn: () => apiService.getCollectors({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Query per ottenere i team (necessaria per il filtro team)
  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => apiService.getTeams({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Configurazione campi per la modale filtri
  const filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Cerca (Donatore, Note)',
      type: 'text',
      icon: <Search />,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'status',
      label: 'Stato',
      type: 'select',
      options: [
        { value: 'PENDING', label: 'In Attesa' },
        { value: 'CONFIRMED', label: 'Confermata' },
        { value: 'CANCELLED', label: 'Annullata' },
        { value: 'REFUNDED', label: 'Rimborsata' },
      ],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'paymentMethod',
      label: 'Metodo di Pagamento',
      type: 'select',
      options: [
        { value: 'CASH', label: 'Contanti' },
        { value: 'CARD', label: 'Carta' },
        { value: 'BANK_TRANSFER', label: 'Bonifico' },
        { value: 'CHECK', label: 'Assegno' },
        { value: 'OTHER', label: 'Altro' },
      ],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'collectorId',
      label: 'Raccoglitore',
      type: 'select',
      options: collectors?.data?.map((collector: Collector) => ({
        value: collector.id,
        label: `${collector.firstName} ${collector.lastName}`,
      })) || [],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'teamId',
      label: 'Team',
      type: 'select',
      options: teams?.data?.map((team: Team) => ({
        value: team.id,
        label: team.name,
      })) || [],
      size: { xs: 12, sm: 6 },
    },
    // Temporaneamente rimosso per evitare conflitti di versioni
    // {
    //   key: 'dateFrom',
    //   label: 'Data Inizio',
    //   type: 'date',
    //   size: { xs: 12, sm: 6 },
    // },
    // {
    //   key: 'dateTo',
    //   label: 'Data Fine',
    //   type: 'date',
    //   size: { xs: 12, sm: 6 },
    // },
    {
      key: 'minAmount',
      label: 'Importo Minimo',
      type: 'number',
      icon: <AttachMoney />,
      placeholder: '0.00',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'maxAmount',
      label: 'Importo Massimo',
      type: 'number',
      icon: <AttachMoney />,
      placeholder: '1000.00',
      size: { xs: 12, sm: 6 },
    },
  ];

  // Valori correnti dei filtri
  const filterValues = {
    search: localFilters.search,
    status: localFilters.status,
    paymentMethod: localFilters.paymentMethod,
    collectorId: localFilters.collectorId,
    teamId: localFilters.teamId,
    dateFrom: localFilters.dateFrom,
    dateTo: localFilters.dateTo,
    minAmount: localFilters.minAmount,
    maxAmount: localFilters.maxAmount,
  };

  // Funzione per applicare i filtri
  const applyFilters = useCallback(() => {
    setQueryFilters(localFilters);
  }, [localFilters]);

  // Funzioni per aggiornare i filtri locali (senza re-render)
  const updateFilter = useCallback((key: string, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = () => {
    setLocalFilters({
      search: '',
      status: '',
      paymentMethod: '',
      collectorId: '',
      teamId: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: '',
    });
    setQueryFilters({
      search: '',
      status: '',
      paymentMethod: '',
      collectorId: '',
      teamId: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: '',
    });
    // Reset alla prima pagina quando si cancellano i filtri
    setPage(0);
  };

  // Genera i campi per la modale di visualizzazione donazione
  const getDonationViewFields = (donation: Donation): ViewField[] => [
    {
      key: 'reference',
      label: 'Riferimento',
      value: donation.reference,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'amount',
      label: 'Importo',
      value: donation.amount,
      type: 'currency',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'paymentMethod',
      label: 'Metodo di Pagamento',
      value: getPaymentMethodLabel(donation.paymentMethod),
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'status',
      label: 'Stato',
      value: donation.status || 'N/A',
      type: 'chip',
      chipColor: 'default',
      chipSx: {
        fontWeight: 500,
        fontSize: '0.75rem',
        backgroundColor: getStatusColor(donation.status).backgroundColor,
        color: getStatusColor(donation.status).color,
        borderColor: getStatusColor(donation.status).borderColor,
        border: '1px solid',
      },
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'donor',
      label: 'Donatore',
      value: donation.donor ? 
        `${donation.donor.firstName} ${donation.donor.lastName}` : 
        'N/A',
      size: { xs: 12, sm: 6 },
      customRender: (value) => (
        <Box>
          <Typography variant="body1">
            {value || 'N/A'}
          </Typography>
          {donation.donor?.email && (
            <Typography variant="body2" color="text.secondary">
              {donation.donor.email}
            </Typography>
          )}
          {donation.donor?.company && (
            <Typography variant="body2" color="text.secondary">
              Azienda: {donation.donor.company}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'collector',
      label: 'Raccoglitore',
      value: donation.collector ? 
        `${donation.collector.firstName} ${donation.collector.lastName}` : 
        'N/A',
      size: { xs: 12, sm: 6 },
      customRender: (value) => (
        <Box>
          <Typography variant="body1">
            {value}
          </Typography>
          {donation.collector?.email && (
            <Typography variant="body2" color="text.secondary">
              {donation.collector.email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'createdAt',
      label: 'Data Creazione',
      value: donation.createdAt,
      type: 'date',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'updatedAt',
      label: 'Ultima Modifica',
      value: donation.updatedAt,
      type: 'date',
      size: { xs: 12, sm: 6 },
    },
    ...(donation.address ? [{
      key: 'address',
      label: 'Indirizzo',
      value: donation.address,
      type: 'address' as const,
      size: { xs: 12 } as const,
    }] : []),
    ...(donation.notes ? [{
      key: 'notes',
      label: 'Note',
      value: donation.notes,
      type: 'notes' as const,
      size: { xs: 12 } as const,
    }] : []),
    ...((donation.latitude && donation.longitude) ? [{
      key: 'coordinates',
      label: 'Coordinate GPS',
      value: `Lat: ${donation.latitude}, Lng: ${donation.longitude}`,
      size: { xs: 12 } as const,
    }] : []),
    ...(donation.documentPhotoS3Key || donation.documentPhotoS3Url ? [{
      key: 'documentPhoto',
      label: 'Foto Documento Incassato',
      value: donation.documentPhotoS3Url,
      type: 'image' as const,
      size: { xs: 12 } as const,
      customRender: () => (
        <SecureImage
          s3Key={donation.documentPhotoS3Key}
          s3Url={donation.documentPhotoS3Url}
          alt="Foto documento incassato"
          maxHeight="150px"
          maxWidth="100%"
          originalName={donation.documentPhotoOriginalName}
          onClick={() => {
            // Usa l'endpoint proxy per apertura in nuova finestra
            if (donation.documentPhotoS3Key) {
              const proxyUrl = apiService.getImageUrl(donation.documentPhotoS3Key);
              window.open(proxyUrl, '_blank');
            } else if (donation.documentPhotoS3Url) {
              window.open(donation.documentPhotoS3Url, '_blank');
            }
          }}
        />
      ),
    }] : []),
    // Cronologia Email
    ...(donation.status === 'CONFIRMED' ? [{
      key: 'emailHistory',
      label: 'Cronologia Email Ricevute',
      value: '',
      size: { xs: 12 } as const,
      customRender: () => {
        // Query per ottenere i log delle email per questa donazione
        const { data: emailLogsData, isLoading: emailLogsLoading, error: emailLogsError } = useQuery({
          queryKey: ['email-logs', donation.id],
          queryFn: () => apiService.getEmailLogsForDonation(donation.id),
          enabled: !!donation.id,
        });
        
        console.log('Email logs debug:', {
          donationId: donation.id,
          emailLogsData,
          emailLogsLoading,
          emailLogsError,
          logs: emailLogsData?.logs
        });
        
        const logs = emailLogsData?.logs || [];
        
        if (emailLogsLoading) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Caricamento cronologia email...
              </Typography>
            </Box>
          );
        }
        
        if (emailLogsError) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="error">
                Errore nel caricamento: {emailLogsError.message}
              </Typography>
            </Box>
          );
        }
        
        if (logs.length === 0) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Nessuna email inviata per questa donazione
              </Typography>
            </Box>
          );
        }

        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History fontSize="small" />
              Cronologia Email ({logs.length} invii)
            </Typography>
            
            <Box 
              sx={{ 
                maxHeight: '200px',
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.default',
                p: 1
              }}
            >
              {logs.map((log, index) => (
                <Box 
                  key={log.id} 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    '&:last-child': { mb: 0 }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getEmailStatusIcon(log.status)}
                    <Typography variant="body2" fontWeight="medium">
                      {getEmailStatusText(log.status)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{index + 1}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Destinatario:</strong> {log.recipientEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Oggetto:</strong> {log.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Creato:</strong> {new Date(log.createdAt).toLocaleString('it-IT')}
                    </Typography>
                    
                    {log.sentAt && (
                      <Typography variant="caption" color="success.main">
                        <strong>Inviato:</strong> {new Date(log.sentAt).toLocaleString('it-IT')}
                      </Typography>
                    )}
                    
                    {log.retryCount > 0 && (
                      <Typography variant="caption" color="warning.main">
                        <strong>Tentativi:</strong> {log.retryCount}/{log.maxRetries}
                      </Typography>
                    )}
                    
                    {log.errorMessage && (
                      <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                        <strong>Errore:</strong> {log.errorMessage}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
      },
    }] : []),
  ];

  const filters: Filters = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    search: queryFilters.search || undefined,
    status: queryFilters.status || undefined,
    paymentMethod: queryFilters.paymentMethod || undefined,
    collectorId: queryFilters.collectorId || undefined,
    teamId: queryFilters.teamId || undefined,
    dateFrom: queryFilters.dateFrom || undefined,
    dateTo: queryFilters.dateTo || undefined,
    minAmount: queryFilters.minAmount ? parseFloat(queryFilters.minAmount) : undefined,
    maxAmount: queryFilters.maxAmount ? parseFloat(queryFilters.maxAmount) : undefined,
  }), [page, rowsPerPage, queryFilters]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['donations', filters],
    queryFn: () => apiService.getDonations(filters),
  });

  // Carica lo stato delle email quando i dati cambiano
  useEffect(() => {
    if (data?.data) {
      data.data.forEach(donation => {
        if (donation.status === 'CONFIRMED' && donation.receipts && donation.receipts.length > 0) {
          loadEmailStatus(donation.id);
        }
      });
    }
  }, [data]);

  const createMutation = useMutation({
    mutationFn: (donation: CreateDonationRequest) => apiService.createDonation(donation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      setOpenDialog(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDonationRequest> }) =>
      apiService.updateDonation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      setOpenDialog(false);
      setEditingDonation(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteDonation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
    },
  });

  const deleteReceiptsMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteDonationReceipts(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      setShowReceiptWarning(false);
      setPendingStatusChange(null);
    },
  });

  const resetForm = () => {
    setFormData({
      amount: 0,
      paymentMethod: 'CASH',
      donorId: '',
      collectorId: user?.id || '',
      notes: '',
      latitude: undefined,
      longitude: undefined,
      address: '',
      reference: '',
      status: 'PENDING',
      documentPhotoS3Key: '',
      documentPhotoS3Url: '',
      documentPhotoOriginalName: '',
      documentPhotoMimeType: '',
      documentPhotoFileSize: 0,
    });
  };

  const handleOpenDialog = (donation?: Donation) => {
    if (donation) {
      console.log('Opening dialog for donation:', donation);
      console.log('Donor ID:', donation.donorId);
      console.log('Collector ID:', donation.collectorId);
      console.log('Donors data:', donors);
      console.log('Donors loading:', donorsLoading);
      
      // Debug per trovare il donatore
      const foundDonor = donors?.find(d => d.id === donation.donorId);
      console.log('Found donor:', foundDonor);
      console.log('Donor IDs in array:', donors?.map(d => d.id));
      console.log('Looking for donor ID:', donation.donorId);
      console.log('Type of donor ID:', typeof donation.donorId);
      console.log('Type of array IDs:', donors?.map(d => typeof d.id));
      
      if (!foundDonor) {
        console.error('Donatore non trovato! ID:', donation.donorId);
        console.error('Donatori disponibili:', donors?.length || 0);
        // Ricarica i donatori per assicurarsi di avere i dati più recenti
        refetchDonors();
      }
      
      // Debug per trovare il raccoglitore (nei collectors)
      const foundCollector = collectors?.data?.find(c => c.id === donation.collectorId);
      console.log('Found collector in collectors:', foundCollector);
      console.log('Collector IDs in array:', collectors?.data?.map(c => c.id));
      console.log('Looking for collector ID:', donation.collectorId);
      
      setEditingDonation(donation);
      setFormData({
        amount: donation.amount,
        paymentMethod: donation.paymentMethod,
        donorId: donation.donorId,
        collectorId: donation.collectorId,
        notes: donation.notes || '',
        latitude: donation.latitude,
        longitude: donation.longitude,
        address: donation.address || '',
        reference: donation.reference || '',
        status: donation.status,
        documentPhotoS3Key: donation.documentPhotoS3Key || '',
        documentPhotoS3Url: donation.documentPhotoS3Url || '',
        documentPhotoOriginalName: donation.documentPhotoOriginalName || '',
        documentPhotoMimeType: donation.documentPhotoMimeType || '',
        documentPhotoFileSize: donation.documentPhotoFileSize || 0,
      });
    } else {
      setEditingDonation(null);
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = async () => {
    // Se c'è un'immagine in staging, fai rollback
    if (formData.documentPhotoS3Key && formData.documentPhotoS3Key.startsWith('staging/')) {
      try {
        await apiService.rollbackStagingFile(formData.documentPhotoS3Key);
      } catch (error) {
        console.error('Errore durante rollback staging:', error);
      }
    }
    
    setOpenDialog(false);
    setEditingDonation(null);
    resetForm();
  };

  // Funzioni per gestire le email
  const loadEmailStatus = async (donationId: string) => {
    try {
      const status = await apiService.getEmailStatus(donationId);
      setEmailStatuses(prev => ({ ...prev, [donationId]: status }));
    } catch (error) {
      console.error('Errore nel caricamento stato email:', error);
    }
  };


  const handleSendEmail = async (donationId: string) => {
    setSendingEmails(prev => new Set(prev).add(donationId));
    try {
      const result = await apiService.sendReceiptEmail(donationId);
      if (result.success) {
        // Ricarica lo stato email
        await loadEmailStatus(donationId);
        // Mostra messaggio di successo
        alert(result.message);
      } else {
        alert(`Errore: ${result.message}`);
      }
    } catch (error) {
      console.error('Errore nell\'invio email:', error);
      alert('Errore nell\'invio dell\'email');
    } finally {
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(donationId);
        return newSet;
      });
    }
  };

  const handleRetryEmail = async (donationId: string) => {
    const emailStatus = emailStatuses[donationId];
    if (!emailStatus?.emailLogId) {
      alert('EmailLogId non trovato. Ricarica la pagina e riprova.');
      return;
    }

    setSendingEmails(prev => new Set(prev).add(donationId));
    try {
      const result = await apiService.retryFailedEmail(emailStatus.emailLogId);
      if (result.success) {
        await loadEmailStatus(donationId);
        alert(result.message);
      } else {
        alert(`Errore: ${result.message}`);
      }
    } catch (error) {
      console.error('Errore nel retry email:', error);
      alert('Errore nel retry dell\'email');
    } finally {
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(donationId);
        return newSet;
      });
    }
  };

  const handleOpenViewDialog = (donation: Donation) => {
    setViewingDonation(donation);
    setViewDialog(true);
    
    // Carica lo stato email se la donazione è confermata
    if (donation.status === 'CONFIRMED') {
      loadEmailStatus(donation.id);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setViewingDonation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Assicurati che l'amount sia sempre un numero
    const dataToSend = {
      ...formData,
      amount: typeof formData.amount === 'string' ? parseFloat(formData.amount) : formData.amount
    };

    try {
      // Se c'è un'immagine in staging, committala prima di salvare
      if (formData.documentPhotoS3Key && formData.documentPhotoS3Key.startsWith('staging/')) {
        const finalKey = formData.documentPhotoS3Key.replace('staging/', 'doc_donazioni/');
        const commitResult = await apiService.commitStagingFile(formData.documentPhotoS3Key, finalKey);
        
        // Aggiorna i dati con la chiave finale
        dataToSend.documentPhotoS3Key = commitResult.s3Key;
        dataToSend.documentPhotoS3Url = commitResult.s3Url;
      }
      
      if (editingDonation) {
        updateMutation.mutate({ id: editingDonation.id, data: dataToSend });
      } else {
        createMutation.mutate(dataToSend);
      }
    } catch (error) {
      console.error('Errore durante commit staging:', error);
      // Fallback: salva senza committare l'immagine
      if (editingDonation) {
        updateMutation.mutate({ id: editingDonation.id, data: dataToSend });
      } else {
        createMutation.mutate(dataToSend);
      }
    }
  };

  const handleDelete = (donation: Donation) => {
    // Se la donazione è confermata, mostra warning
    if (donation.status === 'CONFIRMED') {
      setDonationToDelete(donation);
      setShowDeleteWarning(true);
    } else {
      // Per donazioni non confermate, procedi direttamente
      if (window.confirm('Sei sicuro di voler eliminare questa donazione?')) {
        deleteMutation.mutate(donation.id);
      }
    }
  };

  const confirmDelete = () => {
    if (donationToDelete) {
      deleteMutation.mutate(donationToDelete.id);
      setShowDeleteWarning(false);
      setDonationToDelete(null);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    // Validazione per assicurarsi che la pagina non sia negativa
    if (newPage >= 0) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Validazione per assicurarsi che rowsPerPage sia valido
    if (newRowsPerPage > 0) {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  // Validazione pagina corrente dopo cambio di rowsPerPage
  useEffect(() => {
    if (data?.pagination && data.pagination.total > 0) {
      const maxPage = Math.max(0, Math.ceil(data.pagination.total / rowsPerPage) - 1);
      if (page > maxPage) {
        setPage(maxPage);
      }
    }
  }, [data?.pagination, rowsPerPage, page]);

  const getStatusColor = (status: string) => {
    if (!status) return statusColors.inactive;
    switch (status) {
      case 'CONFIRMED': return statusColors.confirmed;
      case 'PENDING': return statusColors.pending;
      case 'CANCELLED': return statusColors.cancelled;
      case 'REFUNDED': return statusColors.refunded;
      default: return statusColors.inactive;
    }
  };

  const getEmailStatusIcon = (status: string) => {
    switch (status) {
      case 'SENT': return <CheckCircle color="success" fontSize="small" />;
      case 'FAILED': return <Cancel color="error" fontSize="small" />;
      case 'PENDING': return <Schedule color="warning" fontSize="small" />;
      case 'RETRY': return <Refresh color="info" fontSize="small" />;
      default: return <EmailOutlined color="action" fontSize="small" />;
    }
  };

  const getEmailStatusText = (status: string) => {
    switch (status) {
      case 'SENT': return 'Inviata';
      case 'FAILED': return 'Fallita';
      case 'PENDING': return 'In attesa';
      case 'RETRY': return 'Riprova';
      default: return 'Sconosciuto';
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CASH': return 'Contanti';
      case 'CARD': return 'Carta';
      case 'BANK_TRANSFER': return 'Bonifico';
      case 'CHECK': return 'Assegno';
      case 'OTHER': return 'Altro';
      default: return method;
    }
  };

  // Definizione colonne per DataTable
  const columns: Column<Donation>[] = [
    {
      id: 'reference',
      label: 'Riferimento',
      minWidth: 120,
      format: (value, donation) => (
        <Typography variant="body2" fontWeight={500}>
          {donation.reference || '-'}
        </Typography>
      ),
    },
    {
      id: 'donor',
      label: 'Donatore',
      minWidth: 200,
      format: (value, donation) => (
        <Typography variant="body2">
          {donation.donor ? 
            `${donation.donor.firstName} ${donation.donor.lastName}` : 
            'N/A'
          }
        </Typography>
      ),
    },
    {
      id: 'company',
      label: 'Azienda',
      minWidth: 150,
      format: (value, donation) => (
        <Typography variant="body2" color="text.secondary">
          {donation.donor?.company || '-'}
        </Typography>
      ),
    },
    {
      id: 'amount',
      label: 'Importo',
      minWidth: 100,
      align: 'right',
      format: (value, donation) => (
        <Typography variant="body2" fontWeight={600} color="success.main">
          €{Number(donation.amount).toFixed(2)}
        </Typography>
      ),
    },
    {
      id: 'paymentMethod',
      label: 'Metodo',
      minWidth: 120,
      format: (value, donation) => (
        <Typography variant="body2">
          {getPaymentMethodLabel(donation.paymentMethod)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Stato',
      minWidth: 120,
      align: 'center',
      format: (value, donation) => {
        const colors = getStatusColor(donation.status);
        return (
          <Chip
            label={donation.status || 'N/A'}
            variant="default"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              backgroundColor: colors.backgroundColor,
              color: colors.color,
              borderColor: colors.borderColor,
            }}
          />
        );
      },
    },
    {
      id: 'collector',
      label: 'Raccoglitore',
      minWidth: 150,
      format: (value, donation) => (
        <Typography variant="body2">
          {donation.collector ? 
            `${donation.collector.firstName} ${donation.collector.lastName}` : 
            'N/A'
          }
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Data',
      minWidth: 120,
      format: (value, donation) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(donation.createdAt).toLocaleDateString('it-IT')}
        </Typography>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 150,
      align: 'center',
      format: (value, donation) => {
        if (donation.status === 'CONFIRMED' && donation.receipts && donation.receipts.length > 0) {
          const emailStatus = emailStatuses[donation.id];
          const isSending = sendingEmails.has(donation.id);
          
          if (isSending) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="caption">Invio...</Typography>
              </Box>
            );
          }
          
          if (!emailStatus) {
            return (
              <IconButton
                size="small"
                onClick={() => handleSendEmail(donation.id)}
                color="primary"
                title="Invia email ricevuta"
                sx={{
                  color: actionColors.view.color,
                  '&:hover': {
                    backgroundColor: actionColors.view.hoverBackground,
                  },
                }}
              >
                <EmailOutlined />
              </IconButton>
            );
          }
          
          if (emailStatus.hasReceivedEmail) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Email color="success" fontSize="small" />
                <Typography variant="caption" color="success.main" sx={{ mr: 1 }}>
                  Inviata
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleSendEmail(donation.id)}
                  color="primary"
                  title="Invia copia email"
                  sx={{
                    color: actionColors.view.color,
                    '&:hover': {
                      backgroundColor: actionColors.view.hoverBackground,
                    },
                  }}
                >
                  <EmailOutlined />
                </IconButton>
              </Box>
            );
          }
          
          if (emailStatus.lastEmailStatus === 'FAILED' || !emailStatus.hasReceivedEmail) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Error sx={{ color: 'error.main' }} fontSize="small" />
                <IconButton
                  size="small"
                  onClick={() => handleRetryEmail(donation.id)}
                  title={`Riprova invio (${emailStatus.retryCount || 0}/${emailStatus.maxRetries || 3})`}
                  sx={{
                    color: actionColors.delete.color,
                    '&:hover': {
                      backgroundColor: actionColors.delete.hoverBackground,
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Box>
            );
          }
          
          return (
            <IconButton
              size="small"
              onClick={() => handleSendEmail(donation.id)}
              color="primary"
              title="Invia email ricevuta"
              sx={{
                color: actionColors.view.color,
                '&:hover': {
                  backgroundColor: actionColors.view.hoverBackground,
                },
              }}
            >
              <EmailOutlined />
            </IconButton>
          );
        } else {
          return (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          );
        }
      },
    },
    {
      id: 'actions',
      label: 'Azioni',
      minWidth: 120,
      align: 'right',
      format: (value, donation) => (
        <ActionButtons
          onView={() => handleOpenViewDialog(donation)}
          onEdit={() => handleOpenDialog(donation)}
          onDelete={() => handleDelete(donation)}
          showEdit={hasAnyRole(['ADMIN', 'SUPERVISOR'])}
          showDelete={hasAnyRole(['ADMIN', 'SUPERVISOR'])}
          size="small"
          spacing={0.5}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Caricamento donazioni...</Typography>
      </Box>
    );
  }


  return (
    <PageLayout>
      <PageHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Donazioni</Typography>
          <Box display="flex" gap={2}>
            <FilterChip
              onClick={() => setFilterModalOpen(true)}
              hasActiveFilters={hasActiveFilters}
              activeFiltersCount={activeFiltersCount}
            />
            {hasAnyRole(['ADMIN', 'SUPERVISOR']) && (
              <Button
                variant="primary"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Nuova Donazione
              </Button>
            )}
          </Box>
        </Box>
      </PageHeader>

      <ScrollableContent maxHeight="calc(100vh - 400px)">
        <DataTable
          columns={columns}
          data={data?.data || []}
          loading={isLoading}
          error={error?.message}
          emptyMessage="Nessuna donazione trovata"
        />
      </ScrollableContent>
      
      <StandardPagination
        count={data?.pagination.total || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDonation ? 'Modifica Donazione' : 'Nuova Donazione'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {/* Alert informativo per conferma donazione */}
            {(createMutation.isPending || updateMutation.isPending) && formData.status === 'CONFIRMED' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Confermando donazione...</AlertTitle>
                Stiamo generando la ricevuta PDF e inviando l'email al donatore. 
                Questa operazione potrebbe richiedere alcuni secondi.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => {
                      const newStatus = e.target.value as any;
                      const currentStatus = editingDonation?.status;
                      
                      // Se stiamo cambiando da CONFIRMED a un altro status, mostra warning
                      if (currentStatus === 'CONFIRMED' && newStatus !== 'CONFIRMED' && editingDonation?.receipts && editingDonation.receipts.length > 0) {
                        setPendingStatusChange(newStatus);
                        setShowReceiptWarning(true);
                      } else {
                        setFormData({ ...formData, status: newStatus });
                      }
                    }}
                    label="Stato"
                  >
                    <MenuItem value="PENDING">In Attesa</MenuItem>
                    <MenuItem value="CONFIRMED">Confermata</MenuItem>
                    <MenuItem value="CANCELLED">Annullata</MenuItem>
                    <MenuItem value="REFUNDED">Rimborsata</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Importo"
                  type="number"
                  inputProps={{ step: "0.01", min: "0.01" }}
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Metodo di Pagamento</InputLabel>
                  <Select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                    label="Metodo di Pagamento"
                  >
                    <MenuItem value="CASH">Contanti</MenuItem>
                    <MenuItem value="CARD">Carta</MenuItem>
                    <MenuItem value="BANK_TRANSFER">Bonifico</MenuItem>
                    <MenuItem value="CHECK">Assegno</MenuItem>
                    <MenuItem value="OTHER">Altro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {donorsLoading ? (
                  <TextField
                    fullWidth
                    label="Donatore"
                    value="Caricamento donatori..."
                    disabled
                    helperText="Caricamento in corso..."
                  />
                ) : (
                  <Autocomplete
                    options={donors || []}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                    value={donors?.find(d => d.id === formData.donorId) || null}
                    onChange={(_, value) => setFormData({ ...formData, donorId: value?.id || '' })}
                    renderInput={(params) => {
                      const foundDonor = donors?.find(d => d.id === formData.donorId);
                      return (
                        <TextField
                          {...params}
                          label="Donatore"
                          required
                          error={!foundDonor && formData.donorId !== ''}
                          helperText={
                            !donors?.length 
                              ? "Nessun donatore disponibile" 
                              : !foundDonor && formData.donorId !== ''
                              ? "Donatore non trovato - potrebbe essere stato eliminato"
                              : ""
                          }
                        />
                      );
                    }}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {collectorsLoading ? (
                  <TextField
                    fullWidth
                    label="Raccoglitore"
                    value="Caricamento raccoglitori..."
                    disabled
                    helperText="Caricamento in corso..."
                  />
                ) : (
                  <Autocomplete
                    options={collectors?.data || []}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                    value={collectors?.data?.find(c => c.id === formData.collectorId) || null}
                    onChange={(_, value) => setFormData({ ...formData, collectorId: value?.id || '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Raccoglitore"
                        required
                        helperText={!collectors?.data?.length ? "Nessun raccoglitore disponibile" : ""}
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Indirizzo"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Latitudine"
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  value={formData.latitude || ''}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || undefined })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Longitudine"
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  value={formData.longitude || ''}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || undefined })}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Note"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom>
                  Foto Documento Incassato
                </Typography>
                <FileUpload
                  onFileUploaded={(fileData) => {
                    setFormData({
                      ...formData,
                      documentPhotoS3Key: fileData.s3Key,
                      documentPhotoS3Url: fileData.s3Url,
                      documentPhotoOriginalName: fileData.originalName,
                      documentPhotoMimeType: fileData.mimeType,
                      documentPhotoFileSize: fileData.fileSize,
                    });
                  }}
                  onFileRemoved={() => {
                    setFormData({
                      ...formData,
                      documentPhotoS3Key: '',
                      documentPhotoS3Url: '',
                      documentPhotoOriginalName: '',
                      documentPhotoMimeType: '',
                      documentPhotoFileSize: 0,
                    });
                  }}
                  currentFile={
                    formData.documentPhotoS3Key
                      ? {
                          s3Key: formData.documentPhotoS3Key,
                          s3Url: formData.documentPhotoS3Url || '',
                          originalName: formData.documentPhotoOriginalName || '',
                          mimeType: formData.documentPhotoMimeType || '',
                          fileSize: formData.documentPhotoFileSize || 0,
                        }
                      : undefined
                  }
                  accept={{
                    'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
                  }}
                  maxSize={10 * 1024 * 1024} // 10MB
                  label="Trascina qui la foto del documento o clicca per selezionare"
                  helperText="Supportati: JPG, PNG, WebP (max 10MB)"
                  donationId={editingDonation?.id}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Annulla
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={createMutation.isPending || updateMutation.isPending}
              startIcon={
                (createMutation.isPending || updateMutation.isPending) ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {editingDonation ? (
                updateMutation.isPending ? (
                  formData.status === 'CONFIRMED' ? 'Confermando...' : 'Aggiornando...'
                ) : 'Aggiorna'
              ) : (
                createMutation.isPending ? (
                  formData.status === 'CONFIRMED' ? 'Creando e confermando...' : 'Creando...'
                ) : 'Crea'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog di Warning per Cancellazione Ricevute */}
      <Dialog
        open={showReceiptWarning}
        onClose={() => {
          setShowReceiptWarning(false);
          setPendingStatusChange(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Attenzione: Cancellazione Ricevute
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Cambio Status da Confermata</AlertTitle>
            Stai per cambiare lo status della donazione da "Confermata" a "{pendingStatusChange === 'PENDING' ? 'In Attesa' : pendingStatusChange === 'CANCELLED' ? 'Annullata' : pendingStatusChange === 'REFUNDED' ? 'Rimborsata' : pendingStatusChange}".
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Questa donazione ha delle ricevute associate che verranno automaticamente cancellate.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Le ricevute PDF e i file associati verranno eliminati definitivamente dal sistema.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Promemoria Importante</AlertTitle>
            <Typography variant="body2">
              <strong>Ricordati di cancellare manualmente anche il file immagine del documento</strong> se non più necessario, 
              utilizzando la funzione di modifica della donazione. I file delle immagini non vengono eliminati automaticamente 
              per evitare perdite accidentali di dati importanti.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowReceiptWarning(false);
              setPendingStatusChange(null);
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={() => {
              if (editingDonation && pendingStatusChange) {
                // Prima cancella le ricevute
                deleteReceiptsMutation.mutate(editingDonation.id, {
                  onSuccess: () => {
                    // Poi aggiorna lo status
                    setFormData({ ...formData, status: pendingStatusChange });
                    setShowReceiptWarning(false);
                    setPendingStatusChange(null);
                  }
                });
              }
            }}
            variant="primary"
            color="warning"
            disabled={deleteReceiptsMutation.isPending}
          >
            {deleteReceiptsMutation.isPending ? 'Cancellazione...' : 'Conferma e Cancella Ricevute'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog di Warning per Cancellazione Donazione Confermata */}
      <Dialog
        open={showDeleteWarning}
        onClose={() => {
          setShowDeleteWarning(false);
          setDonationToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Attenzione: Cancellazione Donazione Confermata
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Donazione Confermata</AlertTitle>
            Non puoi cancellare una donazione che è già stata confermata.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            La donazione <strong>{donationToDelete?.reference || donationToDelete?.id.substring(0, 8)}</strong> 
            è in stato "Confermata" e non può essere eliminata.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se necessario, puoi cambiare lo status della donazione a "Annullata" o "Rimborsata" 
            utilizzando la funzione di modifica.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Alternative Disponibili</AlertTitle>
            <Typography variant="body2">
              • <strong>Cambia status</strong> a "Annullata" per invalidare la donazione<br/>
              • <strong>Cambia status</strong> a "Rimborsata" se è stato effettuato un rimborso<br/>
              • <strong>Contatta l'amministratore</strong> per casi particolari
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeleteWarning(false);
              setDonationToDelete(null);
            }}
            variant="primary"
          >
            Ho Capito
          </Button>
          <Button
            onClick={() => {
              if (donationToDelete) {
                setShowDeleteWarning(false);
                setDonationToDelete(null);
                handleOpenDialog(donationToDelete);
              }
            }}
            variant="outlined"
            startIcon={<Edit />}
          >
            Modifica Donazione
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale di Visualizzazione Donazione */}
      <ViewModal
        open={viewDialog}
        onClose={handleCloseViewDialog}
        title="Dettagli Donazione"
        fields={viewingDonation ? getDonationViewFields(viewingDonation) : []}
        actions={
          <Box>
            <Button
              variant="outlined"
              onClick={handleCloseViewDialog}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                mr: 1,
              }}
            >
              Chiudi
            </Button>
            {hasAnyRole(['ADMIN', 'SUPERVISOR']) && (
              <Button
                variant="primary"
                onClick={() => {
                  handleCloseViewDialog();
                  handleOpenDialog(viewingDonation);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Modifica
              </Button>
            )}
          </Box>
        }
      />

      {/* GenericFilterModal */}
      <GenericFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filtri Donazioni"
        fields={filterFields}
        values={filterValues}
        onUpdateValue={updateFilter}
        onApply={() => {
          // Reset alla prima pagina quando si applicano i filtri
          setPage(0);
          // Applica i filtri locali alla query
          applyFilters();
        }}
        onClear={clearFilters}
      />
    </PageLayout>
  );
};

export default Donations;
